import { useState } from "react";
import { useForm } from "@tanstack/react-form";
import type { z } from "zod";

import { ApiError, GENERIC_ERROR } from "../../../../utils/api.ts";
import { showError, showSuccess } from "../../../../utils/toast.ts";
import {
  Button,
  Modal,
  normalizeOtpValue,
  OtpInput,
  TextField,
} from "../../../ui/index.ts";
import { formatFormFieldErrors } from "../../auth/forms/form-errors.ts";
import { useExpireCountdown } from "../hooks/use-expire-countdown.ts";
import {
  verifyEmailSchema,
  verifyOtpSchema,
} from "../schemas/onboard-verify.schema.ts";

import styles from "./verify-email-otp-modal.module.css";

export type VerifyEmailOtpModalProps = {
  open: boolean;
  variant: "personal" | "work" | "invitation";
  token: string;
  onGenerateOtp: (email: string) => Promise<{ expireAt: string }>;
  onValidate: (email: string, otp: string) => Promise<void>;
};

type VerifyEmailFormValues = z.infer<typeof verifyEmailSchema>;
type VerifyOtpFormValues = z.infer<typeof verifyOtpSchema>;

type Step = "email" | "otp";

const EMAIL_DEFAULTS: VerifyEmailFormValues = { email: "" };
const OTP_DEFAULTS: VerifyOtpFormValues = { otp: "" };

const VARIANT_COPY = {
  invitation: {
    lead: "Verify the email address where you received this invitation.",
    label: "Email",
    emailFieldId: "onboard-verify-invitation-email",
  },
  personal: {
    lead: "Verify your personal email on which you received the onboard form link.",
    label: "Personal Email",
    emailFieldId: "onboard-verify-personal-email",
  },
  work: {
    lead: "Verify your work email on which you received the onboard form link.",
    label: "Work Email",
    emailFieldId: "onboard-verify-work-email",
  },
} as const;

function formatCountdown(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return GENERIC_ERROR;
}

export function VerifyEmailOtpModal({
  open,
  variant,
  token: _token,
  onGenerateOtp,
  onValidate,
}: VerifyEmailOtpModalProps) {
  const copy = VARIANT_COPY[variant];
  const [step, setStep] = useState<Step>("email");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [expireAt, setExpireAt] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { timeLeft, isExpired, resetExpireAt } = useExpireCountdown(expireAt);

  const emailForm = useForm({
    defaultValues: EMAIL_DEFAULTS,
    validators: { onSubmit: verifyEmailSchema },
    onSubmit: async ({ value }) => {
      setSubmitting(true);
      try {
        const email = value.email.trim();
        const { expireAt: nextExpireAt } = await onGenerateOtp(email);
        showSuccess("Verification code sent to your email.");
        setVerifiedEmail(email);
        setExpireAt(nextExpireAt);
        resetExpireAt(nextExpireAt);
        setStep("otp");
      } catch (e) {
        showError(errorMessage(e));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const otpForm = useForm({
    defaultValues: OTP_DEFAULTS,
    validators: { onSubmit: verifyOtpSchema },
    onSubmit: async ({ value }) => {
      setSubmitting(true);
      try {
        await onValidate(verifiedEmail, normalizeOtpValue(value.otp));
        showSuccess("Email verified successfully.");
      } catch (e) {
        showError(errorMessage(e));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleEmailSubmit = () => {
    void emailForm.handleSubmit();
  };

  const handleOtpSubmit = () => {
    void otpForm.handleSubmit();
  };

  const handleResend = async () => {
    setSubmitting(true);
    try {
      const { expireAt: nextExpireAt } = await onGenerateOtp(verifiedEmail);
      showSuccess("A new verification code has been sent.");
      setExpireAt(nextExpireAt);
      resetExpireAt(nextExpireAt);
    } catch (e) {
      showError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeEmail = () => {
    setStep("email");
  };

  if (step === "otp") {
    return (
      <Modal
        open={open}
        onClose={() => {}}
        title="Verify Email"
        size="md"
        closeOnBackdropClick={false}
        closeOnEscape={false}
        hideCloseButton
        footer={
          <div className={styles.footer}>
            <Button
              type="button"
              fullWidth
              disabled={submitting || isExpired}
              onClick={handleOtpSubmit}
            >
              {submitting ? "Verifying…" : "Verify"}
            </Button>
          </div>
        }
      >
        <p className={`body-sm ${styles.lead}`}>
          Enter the verification code sent to{" "}
          <span className={styles.emailHighlight}>{verifiedEmail}</span>
        </p>
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            handleOtpSubmit();
          }}
          noValidate
        >
          <otpForm.Field name="otp">
            {(field) => (
              <div className={styles.otpFieldWrap}>
                <OtpInput
                  id="onboard-verify-otp"
                  label="Verification Code (OTP)"
                  required
                  value={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                  error={formatFormFieldErrors(field.state.meta.errors)}
                  disabled={submitting}
                />
              </div>
            )}
          </otpForm.Field>

          <div className={styles.otpActions}>
            {isExpired ? (
              <Button
                type="button"
                variant="accent-ghost"
                size="sm"
                disabled={submitting}
                onClick={() => void handleResend()}
              >
                Resend code
              </Button>
            ) : (
              <p className={`body-sm ${styles.countdown}`}>
                Code expires in {formatCountdown(timeLeft)}
              </p>
            )}
            <button
              type="button"
              className={`${styles.textButton} body-sm`}
              disabled={submitting}
              onClick={handleChangeEmail}
            >
              Change email
            </button>
          </div>
        </form>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={() => {}}
      title="Verify Email"
      size="sm"
      closeOnBackdropClick={false}
      closeOnEscape={false}
      hideCloseButton
      footer={
        <div className={styles.footer}>
          <Button
            type="button"
            fullWidth
            disabled={submitting}
            onClick={handleEmailSubmit}
          >
            {submitting ? "Sending…" : "Continue"}
          </Button>
        </div>
      }
    >
      <p className={`body-sm ${styles.lead}`}>{copy.lead}</p>
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          handleEmailSubmit();
        }}
        noValidate
      >
        <emailForm.Field name="email">
          {(field) => (
            <TextField
              id={copy.emailFieldId}
              label={copy.label}
              type="email"
              autoComplete="email"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              error={formatFormFieldErrors(field.state.meta.errors)}
              disabled={submitting}
              required
            />
          )}
        </emailForm.Field>
      </form>
    </Modal>
  );
}
